/**
 * Config monete d'oro singole per /monete-oro/:slug
 * Target SEO: query "quotazione krugerrand", "quanto vale sterlina oro", ecc.
 */

export interface MonetaOro {
  slug: string;
  nome: string;
  titleTag: string;       // ≤60
  metaDesc: string;       // 120-160
  h1: string;
  pesoTotaleG: number;    // grammi totali
  pesoOroFinoG: number;   // grammi oro fino
  titolo: string;         // es. "917‰"
  caratura: string;       // es. "22kt"
  diametroMm: number;
  paese: string;
  annoIntroduzione: number;
  intro: string;
  premiumInfo: string;
  comeRiconoscerla: string[];
  faqs: Array<{ q: string; a: string }>;
}

export const MONETE_ORO: Record<string, MonetaOro> = {
  krugerrand: {
    slug: 'krugerrand',
    nome: 'Krugerrand',
    titleTag: 'Quotazione Krugerrand Oro Oggi | Prezzo 1oz | Gildy',
    metaDesc:
      'Quotazione Krugerrand sudafricano aggiornata. Peso 33,93g, 1oz oro fino, titolo 916‰. Premium di mercato e dove venderlo in Italia.',
    h1: 'Quanto vale un Krugerrand oggi',
    pesoTotaleG: 33.93,
    pesoOroFinoG: 31.1035,
    titolo: '916‰',
    caratura: '22kt',
    diametroMm: 32.6,
    paese: 'Sudafrica',
    annoIntroduzione: 1967,
    intro:
      'Il Krugerrand è la moneta da investimento più diffusa al mondo: prodotta dalla South African Mint dal 1967, contiene esattamente 1 oncia troy (31,1035g) di oro fino in lega 916‰ con rame, per un peso totale di 33,93g. La lega ramata le dà il caratteristico colore rosso-arancio e una durezza superiore alle monete in oro puro.',
    premiumInfo:
      'Il premium tipico sul Krugerrand in Italia è del 3-6% sul valore spot per esemplari in capsula originale, sale al 7-10% per annate rare (1967, 1969).',
    comeRiconoscerla: [
      'Diritto: profilo del presidente Paul Kruger.',
      'Rovescio: antilope springbok, simbolo del Sudafrica.',
      'Bordo zigrinato, peso 33,93g esatti su bilancia di precisione.',
      'Suona "argentina" se rimbalzata su superficie dura (test del suono).',
      'Le contraffazioni in tungsteno hanno peso e diametro corretti ma densità diversa: test ad ultrasuoni per certezza.',
    ],
    faqs: [
      {
        q: 'Il Krugerrand ha valore numismatico?',
        a: 'Generalmente no: è una moneta da investimento bullion, il valore segue il prezzo spot dell\'oro. Solo le annate 1967 e 1969 in conservazione FDC hanno premium aggiuntivo.',
      },
      {
        q: 'Conviene di più del lingotto?',
        a: 'A parità di peso oro fino, il Krugerrand ha premium leggermente più alto del lingotto (3-6% vs 1-3%) ma è più liquido e riconoscibile a livello globale.',
      },
    ],
  },
  sterlina: {
    slug: 'sterlina',
    nome: 'Sterlina d\'oro',
    titleTag: 'Quotazione Sterlina d\'Oro Oggi | Sovereign | Gildy',
    metaDesc:
      'Quotazione sterlina d\'oro (Sovereign) aggiornata. Peso 7,98g totali, 7,32g oro fino, 22kt. Premium numismatico e dove venderla.',
    h1: 'Quanto vale una sterlina d\'oro oggi',
    pesoTotaleG: 7.98805,
    pesoOroFinoG: 7.32238,
    titolo: '917‰',
    caratura: '22kt',
    diametroMm: 22.05,
    paese: 'Regno Unito',
    annoIntroduzione: 1817,
    intro:
      'La sterlina d\'oro (Sovereign) è la moneta da investimento più antica e diffusa in Europa: coniata dalla Royal Mint dal 1817, pesa 7,98g con titolo 917‰ (22kt), pari a 7,32g di oro fino. Esistono mezze sterline (3,99g) e doppie sterline (15,98g). Il diritto raffigura il sovrano in carica, il rovescio San Giorgio che uccide il drago.',
    premiumInfo:
      'Premium tipico 3-6% sul valore oro per sterline moderne (post 1957). Sterline vittoriane (1837-1901) e georgiane possono valere 10-30% in più del puro oro.',
    comeRiconoscerla: [
      'Diametro standard 22,05 mm, peso 7,98g esatti.',
      'Diritto: ritratto del monarca regnante (Elisabetta II, Carlo III, vittime precedenti).',
      'Rovescio: San Giorgio a cavallo che uccide il drago (incisione Pistrucci).',
      'Le sterline antiche (pre-1900) hanno spesso usura sui rilievi: la patina naturale è un indizio di autenticità.',
      'Test peso + diametro + suono per autenticità base; XRF per certezza.',
    ],
    faqs: [
      {
        q: 'Quanto pesa una sterlina d\'oro?',
        a: '7,98 grammi totali, di cui 7,32 grammi di oro fino (titolo 917‰, lega con rame).',
      },
      {
        q: 'La mezza sterlina vale la metà?',
        a: 'Sì come valore oro (3,66g fini), ma il premium percentuale è leggermente più alto per via dei costi fissi di conio.',
      },
    ],
  },
  marengo: {
    slug: 'marengo',
    nome: 'Marengo d\'oro',
    titleTag: 'Quotazione Marengo d\'Oro Oggi | 20 Franchi | Gildy',
    metaDesc:
      'Quotazione marengo d\'oro 20 franchi aggiornata. Peso 6,45g, 5,81g oro fino, 900‰. Varianti italiana, francese, svizzera e belga.',
    h1: 'Quanto vale un marengo d\'oro oggi',
    pesoTotaleG: 6.45161,
    pesoOroFinoG: 5.80645,
    titolo: '900‰',
    caratura: '21.6kt',
    diametroMm: 21,
    paese: 'Italia/Francia/Svizzera/Belgio',
    annoIntroduzione: 1801,
    intro:
      'Il marengo è la moneta da 20 franchi in oro 900‰ coniata in Italia, Francia, Svizzera, Belgio e altri paesi dell\'Unione Monetaria Latina dal 1801 al 1927. Pesa 6,45g con titolo 900‰ (21,6kt), pari a 5,81g di oro fino. Tutte le varianti hanno lo stesso peso oro e stessa quotazione di base, ma i marenghi italiani rari possono avere premium significativo.',
    premiumInfo:
      'Premium tipico 5-10% sul valore oro per Vreneli svizzeri e Napoleoni francesi standard. Marenghi italiani (Umberto I 1879, Vittorio Emanuele III) possono raggiungere 20-50% in più.',
    comeRiconoscerla: [
      'Diametro 21 mm, peso 6,45g esatti, bordo zigrinato.',
      'Marengo italiano: profilo del re (Vittorio Emanuele II, Umberto I, Vittorio Emanuele III).',
      'Vreneli svizzero: testa femminile con scritta HELVETIA, è il più diffuso.',
      'Napoleone francese: testa di Napoleone o testa di Cerere (varie tipologie).',
      'I marenghi belghi (Leopoldo II) sono i meno richiesti, premium minimo.',
    ],
    faqs: [
      {
        q: 'Quanto oro contiene un marengo?',
        a: '5,81 grammi di oro fino. La differenza con il peso totale (6,45g) è data dalla lega di rame.',
      },
      {
        q: 'Il marengo italiano vale più del francese?',
        a: 'In media sì, soprattutto per Umberto I 1879 e Vittorio Emanuele III rari, che possono valere 2-5 volte il valore oro.',
      },
    ],
  },
  vrenelli: {
    slug: 'vrenelli',
    nome: 'Vrenelli (20 franchi svizzeri)',
    titleTag: 'Quotazione Vrenelli Oro 20 Franchi Svizzeri | Gildy',
    metaDesc:
      'Quotazione Vrenelli (20 franchi svizzeri) aggiornata. Peso 6,45g, 5,81g oro fino. La moneta da investimento svizzera più diffusa in Europa.',
    h1: 'Quanto vale un Vrenelli svizzero oggi',
    pesoTotaleG: 6.45161,
    pesoOroFinoG: 5.80645,
    titolo: '900‰',
    caratura: '21.6kt',
    diametroMm: 21,
    paese: 'Svizzera',
    annoIntroduzione: 1897,
    intro:
      'Il Vrenelli (o Vreneli) è la moneta svizzera da 20 franchi in oro 900‰, coniata dal 1897 al 1949. Variante svizzera del marengo, pesa 6,45g con 5,81g di oro fino. Il nome deriva dal diminutivo di Verena, la figura femminile (Helvetia) raffigurata sul diritto. È una delle monete da investimento più liquide in Europa per la sua diffusione storica.',
    premiumInfo:
      'Premium tipico 4-7% sul valore oro per Vrenelli standard in buone condizioni. Annate 1897-1900 e 1935 LB hanno premium più alto (10-15%).',
    comeRiconoscerla: [
      'Diritto: testa femminile (Helvetia) con corona di stelle alpine.',
      'Rovescio: stemma svizzero con croce, valore "20 FR" e anno.',
      'Diametro 21 mm, peso 6,45g, bordo con stelle in rilievo.',
      'Le annate più rare (1897, 1935 LB con riconio) richiedono perizia numismatica.',
    ],
    faqs: [
      {
        q: 'Vrenelli e marengo sono la stessa cosa?',
        a: 'Stessa specifica metallica (6,45g totali, 5,81g oro fino, 900‰) ma origine diversa. Il Vrenelli è la versione svizzera, parte dell\'Unione Monetaria Latina.',
      },
    ],
  },
  eagle: {
    slug: 'eagle',
    nome: 'American Gold Eagle',
    titleTag: 'Quotazione American Gold Eagle 1oz Oggi | Gildy',
    metaDesc:
      'Quotazione American Gold Eagle aggiornata. 1 oncia oro fino, 33,93g totali, titolo 916‰. La moneta bullion ufficiale degli Stati Uniti.',
    h1: 'Quanto vale un American Gold Eagle oggi',
    pesoTotaleG: 33.93,
    pesoOroFinoG: 31.1035,
    titolo: '916‰',
    caratura: '22kt',
    diametroMm: 32.7,
    paese: 'Stati Uniti',
    annoIntroduzione: 1986,
    intro:
      'L\'American Gold Eagle è la moneta bullion ufficiale degli Stati Uniti, coniata dalla US Mint dal 1986. Contiene 1 oncia troy (31,1035g) di oro fino in lega 916‰ con rame e argento (per durezza), peso totale 33,93g. Esistono tagli da 1/2, 1/4, 1/10 oz. È riserva monetaria ufficiale del governo USA, quindi a corso legale (valore facciale $50).',
    premiumInfo:
      'Premium tipico 4-8% sul valore oro per esemplari in capsula. Edizioni proof e annate commemorative hanno premium 15-30%.',
    comeRiconoscerla: [
      'Diritto: Liberty con torcia (design di Augustus Saint-Gaudens, 1907).',
      'Rovescio: famiglia di aquile (design di Miley Busiek, dal 1986 al 2021), nuovo design Eagle (dal 2021).',
      'Bordo zigrinato, peso 33,93g esatti.',
      'Lega ramata: colore leggermente arancio simile al Krugerrand.',
    ],
    faqs: [
      {
        q: 'Eagle o Krugerrand: quale conviene?',
        a: 'Stesso peso oro (1oz). L\'Eagle ha premium leggermente più alto (4-8% vs 3-6%) ma è più riconosciuta sul mercato americano.',
      },
    ],
  },
  'maple-leaf': {
    slug: 'maple-leaf',
    nome: 'Canadian Maple Leaf',
    titleTag: 'Quotazione Maple Leaf Oro Canada 1oz Oggi | Gildy',
    metaDesc:
      'Quotazione Canadian Maple Leaf aggiornata. 1oz oro fino 999,9‰ (oro puro 24kt). La moneta bullion più pura al mondo, coniata dal 1979.',
    h1: 'Quanto vale un Maple Leaf canadese oggi',
    pesoTotaleG: 31.1035,
    pesoOroFinoG: 31.1035,
    titolo: '999,9‰',
    caratura: '24kt',
    diametroMm: 30,
    paese: 'Canada',
    annoIntroduzione: 1979,
    intro:
      'Il Canadian Maple Leaf è la moneta bullion canadese coniata dalla Royal Canadian Mint dal 1979. È in oro 999,9‰ puro (24kt), senza leghe: 1 oncia troy pesa esattamente 31,1035g di oro fino. Per via della morbidezza dell\'oro puro va maneggiata con cura: graffi visibili riducono il premium del 10-15%.',
    premiumInfo:
      'Premium tipico 3-6% sul valore oro per esemplari in capsula originale. Edizioni speciali (Snow Leopard, Predator) hanno premium 20-40%.',
    comeRiconoscerla: [
      'Diritto: profilo di Elisabetta II (fino al 2022) o Carlo III (dal 2023).',
      'Rovescio: foglia d\'acero canadese stilizzata.',
      'Dal 2013 include micro-incisione laser anticontraffazione (foglia d\'acero piccola a sinistra).',
      'Oro puro 24kt: colore giallo intenso, peso 31,1035g esatti.',
    ],
    faqs: [
      {
        q: 'Il Maple Leaf è oro puro?',
        a: 'Sì, 999,9‰ (24kt), la purezza più alta tra le monete bullion principali (Krugerrand ed Eagle sono 916‰).',
      },
      {
        q: 'Si graffia facilmente?',
        a: 'Sì, l\'oro puro è morbido. Tienila sempre in capsula originale: graffi visibili riducono il premium.',
      },
    ],
  },
  panda: {
    slug: 'panda',
    nome: 'Chinese Gold Panda',
    titleTag: 'Quotazione Chinese Gold Panda Oro Oggi | Gildy',
    metaDesc:
      'Quotazione Chinese Gold Panda aggiornata. Oro puro 999‰, design panda diverso ogni anno. Forte componente numismatica oltre al valore bullion.',
    h1: 'Quanto vale un Chinese Gold Panda oggi',
    pesoTotaleG: 30,
    pesoOroFinoG: 30,
    titolo: '999‰',
    caratura: '24kt',
    diametroMm: 32,
    paese: 'Cina',
    annoIntroduzione: 1982,
    intro:
      'Il Chinese Gold Panda è la moneta bullion cinese coniata dalla People\'s Bank of China dal 1982. Dal 2016 il peso è espresso in grammi (non più once): 30g per la moneta principale. È in oro 999‰ puro (24kt). Caratteristica unica: il design del panda cambia ogni anno, conferendo forte componente numismatica oltre al valore oro.',
    premiumInfo:
      'Premium tipico 5-10% sul valore oro per annate recenti. Annate storiche (1982-1990) e tirature limitate possono raggiungere 30-100% di premium.',
    comeRiconoscerla: [
      'Diritto: Tempio del Cielo di Pechino, scritta "中华人民共和国" (Repubblica Popolare Cinese).',
      'Rovescio: panda gigante in pose diverse ogni anno (eccezione 2001-2002, stesso design).',
      'Dal 2016 espresso in grammi: 1g, 3g, 8g, 15g, 30g.',
      'Oro puro 999‰, peso esatto certificato.',
    ],
    faqs: [
      {
        q: 'Perché il design cambia ogni anno?',
        a: 'Scelta della People\'s Bank of China per stimolare il collezionismo. Crea forte premium numismatico oltre al valore bullion.',
      },
      {
        q: 'Conviene di più di un Maple Leaf?',
        a: 'Stesso oro puro 24kt, ma il Panda ha premium più alto per il collezionismo. Per puro investimento il Maple Leaf è più efficiente, per collezione il Panda apprezza nel tempo.',
      },
    ],
  },
};

export const MONETE_SLUGS = Object.keys(MONETE_ORO);
export const isValidMoneta = (s?: string): boolean => !!s && s in MONETE_ORO;
export const getMoneta = (s: string): MonetaOro | undefined => MONETE_ORO[s];
